import type { PluginSourceInfo } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { Cloud, CloudDownload, Package } from "lucide-react";
import { cn } from "../lib/utils";
import {
    getPluginSourcePrimaryUrl,
    normalizePluginSourceKind,
    pluginSourceLabelKey,
    pluginSourceTooltipKey,
} from "../plugins/plugin-source-metadata";

export function PluginSourceIcon({
    source,
    size = 12,
    className,
}: {
    source?: PluginSourceInfo;
    size?: number;
    className?: string;
}) {
    const kind = normalizePluginSourceKind(source);
    const classes = cn(
        "shrink-0",
        kind === "local" ? "text-muted-foreground" : "text-primary",
        className,
    );
    if (kind === "cloud-worker") return <Cloud size={size} className={classes} />;
    if (kind === "cloud-bundle") {
        return <CloudDownload size={size} className={classes} />;
    }
    return <Package size={size} className={classes} />;
}

export function PluginSourceBadge({
    source,
    compact = false,
    className,
}: {
    source?: PluginSourceInfo;
    compact?: boolean;
    className?: string;
}) {
    const t = useT("editor");
    const kind = normalizePluginSourceKind(source);
    const label = t(pluginSourceLabelKey(kind));
    const sourceUrl = getPluginSourcePrimaryUrl(source);
    const tooltip = sourceUrl
        ? `${t(pluginSourceTooltipKey(kind))}: ${sourceUrl}`
        : t(pluginSourceTooltipKey(kind));

    return (
        <span
            title={tooltip}
            className={cn(
                "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px]",
                kind === "local"
                    ? "border-border/50 bg-muted/30 text-muted-foreground"
                    : "border-primary/25 bg-primary/5 text-primary",
                className,
            )}
        >
            <PluginSourceIcon source={source} size={compact ? 10 : 11} />
            {compact ? null : <span>{label}</span>}
        </span>
    );
}
